import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Copy, ShieldAlert } from 'lucide-react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

interface Props {
  // Raw `mp_...` token. SECURITY: lives only in the dialog's render path; when
  // the parent clears its state, this prop becomes null and the value is gone
  // from memory. NEVER log it, persist it, or attach it to analytics/Sentry.
  token: string | null
  onClose: () => void
}

export function TokenRevealDialog({ token, onClose }: Props) {
  const { t } = useTranslation('settings')
  const [copied, setCopied] = useState(false)
  const buttonRef = useRef<HTMLButtonElement | null>(null)

  const handleCopy = async () => {
    if (!token) return
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(token)
      } else {
        // Insecure-context fallback (e.g. http://localhost without https).
        const textarea = document.createElement('textarea')
        textarea.value = token
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
      }
      setCopied(true)
      toast.success(t('integrationsPage.reveal.copied'))
    } catch {
      toast.error(t('integrationsPage.reveal.copied'))
    }
  }

  return (
    <Dialog
      open={token !== null}
      onOpenChange={() => {
        // Block all dismiss paths except the explicit "I've copied it" button.
        // The raw token cannot be recovered from the server once dismissed.
      }}
    >
      <DialogContent
        showCloseButton={false}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          buttonRef.current?.focus()
        }}
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <ShieldAlert className="size-5 text-amber-500" />
            <DialogTitle>{t('integrationsPage.reveal.title')}</DialogTitle>
          </div>
          <DialogDescription>
            {t('integrationsPage.reveal.warning')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-stretch gap-2">
          <code
            aria-label={t('integrationsPage.reveal.tokenAriaLabel')}
            className="flex-1 min-w-0 rounded border border-input bg-muted px-3 py-2 font-mono text-sm break-all select-all"
          >
            {token ?? ''}
          </code>
          <Button
            ref={buttonRef}
            type="button"
            variant="secondary"
            onClick={handleCopy}
            aria-label={t('integrationsPage.reveal.copy')}
          >
            {copied ? (
              <Check className="size-4" />
            ) : (
              <Copy className="size-4" />
            )}
            <span className="ml-2">{t('integrationsPage.reveal.copy')}</span>
          </Button>
        </div>

        <div className="flex justify-end pt-2">
          <Button type="button" onClick={onClose}>
            {t('integrationsPage.reveal.iveCopied')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
